import { useEffect, useState } from "react";
import { useJurisdictionOptions, type JurisdictionType } from "../../hooks/useJurisdictionOptions";
import { SearchableSelect } from "../Form/SearchableSelect";

type JurisdictionPickerProps = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
};

/**
 * Region/country jurisdiction picker — the same two-axis selector used by
 * SubscriptionForm and PackageForm, extracted as a standalone component.
 *
 * Renders a toggle (Region | Country) and a searchable select whose option
 * list switches accordingly. The selected `value` is always the raw
 * jurisdiction string (a fixed-region sentinel like "EMEA"/"GLOBAL" or a
 * country/editorial taxonomy id).
 */
export function JurisdictionPicker({ value, onChange, disabled = false }: JurisdictionPickerProps) {
  const { regionOptions, countryOptions, loading } = useJurisdictionOptions();

  const [locationType, setLocationType] = useState<JurisdictionType>("country");

  useEffect(() => {
    if (value && regionOptions.length > 0) {
      setLocationType(regionOptions.some(r => r.value === value) ? "region" : "country");
    }
  }, [value, regionOptions]);

  function changeLocationType(next: JurisdictionType) {
    if (next !== locationType) {
      setLocationType(next);
      onChange("");
    }
  }

  const options = locationType === "region" ? regionOptions : countryOptions;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
      <div
        style={{ display: "flex", gap: "1rem" }}
        role="radiogroup"
        aria-label="Jurisdiction type"
      >
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.375rem",
            fontSize: "var(--font-size-sm)",
            cursor: disabled ? "default" : "pointer",
          }}
        >
          <input
            type="radio"
            name="jurisdictionType"
            value="region"
            checked={locationType === "region"}
            onChange={() => changeLocationType("region")}
            disabled={disabled}
          />
          Region
        </label>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.375rem",
            fontSize: "var(--font-size-sm)",
            cursor: disabled ? "default" : "pointer",
          }}
        >
          <input
            type="radio"
            name="jurisdictionType"
            value="country"
            checked={locationType === "country"}
            onChange={() => changeLocationType("country")}
            disabled={disabled}
          />
          Country
        </label>
      </div>

      <SearchableSelect
        name="jurisdictionId"
        label={locationType === "region" ? "Region" : "Country"}
        value={value || null}
        options={options}
        onChange={selected => onChange(selected !== null ? String(selected) : "")}
        disabled={disabled || loading}
        placeholder={
          loading
            ? "Loading…"
            : locationType === "region"
              ? "Search for a region…"
              : "Search for a country…"
        }
      />
    </div>
  );
}
